// =============================================================================
// Barcode / RFID scanner integration.
// Almost all retail barcode and RFID readers act as USB HID "keyboard wedges":
// they type the scanned code very quickly and finish with Enter. This hook
// listens globally for that fast-keystroke pattern and fires onScan with the
// decoded value — so a scan registers anywhere in the app without the cashier
// first clicking into a field. When an editable field already has focus we do
// nothing and let the field receive the keystrokes natively (e.g. the Sales
// Register's product-code box), which avoids double-processing a scan.
// =============================================================================
import { useEffect, useRef } from 'react'

function isEditableTarget(el: Element | null): boolean {
  if (!el) return false
  const tag = el.tagName
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true
  return (el as HTMLElement).isContentEditable === true
}

export interface ScannerOptions {
  minLength?: number     // ignore buffers shorter than this (avoid stray keys)
  interKeyMs?: number    // max gap between keystrokes to still count as one scan
  enabled?: boolean
}

export function useBarcodeScanner(onScan: (code: string) => void, opts: ScannerOptions = {}): void {
  const { minLength = 3, interKeyMs = 50, enabled = true } = opts
  const buffer = useRef('')
  const lastTime = useRef(0)
  const cb = useRef(onScan)
  cb.current = onScan

  useEffect(() => {
    if (!enabled) return
    const handler = (e: KeyboardEvent) => {
      // Let focused inputs handle their own keystrokes (native wedge entry).
      if (isEditableTarget(document.activeElement)) return
      if (e.ctrlKey || e.altKey || e.metaKey) return

      const now = Date.now()
      if (now - lastTime.current > interKeyMs) buffer.current = ''
      lastTime.current = now

      if (e.key === 'Enter') {
        const code = buffer.current
        buffer.current = ''
        if (code.length >= minLength) {
          e.preventDefault()
          cb.current(code)
        }
        return
      }
      if (e.key.length === 1) buffer.current += e.key
    }

    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [enabled, minLength, interKeyMs])
}
